import { Dialog } from "primereact/dialog";
import { TenantModel } from "../../model/data/tenant";
import React, { useEffect, useState } from "react";
import { SelectButton } from "primereact/selectbutton";
import { Checkbox } from "primereact/checkbox";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { validateTenant } from "./formValidator";
import { Dropdown } from "primereact/dropdown";
import { createSettings } from "../../service/common/commonService";
import { useDispatch, useSelector } from "react-redux";

interface Props {
  open: boolean;
  isEdit: boolean;
  isLoading: boolean;
  handleClose: () => void;
  data: any | null;
  setData: (data: any) => void;
  handleSubmit: (data: TenantModel) => void;
}

interface CodeNameOption {
  name: string;
  code: string;
}

interface LabelValueOption {
  label: string;
  value: boolean;
}

export const CreateTenant: React.FC<Props> = ({
  handleClose,
  data,
  open,
  isEdit,
  isLoading,
  setData,
  handleSubmit,
}) => {
  const [error, setError] = useState<{ [name: string]: string } | {}>({});
  const [selectedResidentStatus, setSelectedResidentStatus] = useState<{ name: string, code: string } | null>(null);
  const dispatch = useDispatch();
  const localization = useSelector((state: any) => state.localization);

  const inputTemp = [
    {
      type: "text",
      name: "name",
      label: localization?.TenantName || "Tenant Name",
      placeholder: localization?.EnterTenantName || "Enter tenant name",
    },
    {
      type: "text",
      name: "tenancyName",
      label: localization?.BusinessName || "Business Name",
      placeholder: localization?.EnterBusinessName || "Enter business name",
    },
    {
      type: "dropdown",
      name: "residentStatus",
      label: localization?.ResidentStatus || "Resident Status",
      placeholder: localization?.SelectAResidentOption || "Select a resident option",
      options: [
        { name: localization?.Resident || "Resident", code: "resident" },
        { name: localization?.NonResident || "Non-resident", code: "non_resident" },
      ] as CodeNameOption[],
    },
    // Only show these fields when NOT in edit mode
    ...(!isEdit
      ? [
        {
          type: "text",
          name: "userName",
          label: localization?.DefaultUserName || "Default User Name",
          placeholder: localization?.EnterDefaultUserName || "Enter default user name",
        },
        {
          type: "text",
          name: "emailAddress",
          label: localization?.DefaultUserEmail || "Default User's Email",
          placeholder: localization?.EnterDefaultUserEmail || "Enter default user's email",
        },
        {
          type: "checkbox",
          name: "connectionStringCheckBox",
          label: localization?.AddConnectionString || "Do you want to add connection string?",
        },
        ...(data?.connectionStringCheckBox
          ? [
            {
              type: "text",
              name: "connectionString",
              label: localization?.ConnectionString || "Connection String",
              placeholder:
                localization?.ConnectionStringPlaceholder || "postgres://userName:password@hostName:portNumber/databaseName",
            },
          ]
          : []),
      ]
      : []),
    {
      type: "select",
      name: "isActive",
      label: localization?.Status || "Status",
      placeholder: localization?.SelectStatus || "Select Status",
      options: [
        { label: localization?.Active || "Active", value: true },
        { label: localization?.Inactive || "Inactive", value: false },
      ] as LabelValueOption[],
    },
  ];

  const handleInputChange = (
    field: keyof TenantModel | "defaultUserName",
    value: any
  ) => {


    setData((prevData: any) => {
      const isDefaultUserField = ["userName", "emailAddress"].includes(
        field as string
      );

      // ✅ Remove connectionString if value is null/undefined
      if (
        (value === false) &&
        field === "connectionStringCheckBox"
      ) {
        const { connectionString, ...rest } = prevData;
        return {
          ...rest,
          connectionStringCheckBox: false, // 👈 keep checkbox controlled
        };
      }

      // ❌ Ignore other null / undefined values
      if (value === null || value === undefined) {
        return prevData;
      }

      return {
        ...prevData,
        // Ensure defaultUser fields update properly
        defaultUser: isDefaultUserField
          ? {
            ...prevData.defaultUser,
            [field]: value, // Properly update the correct field
          }
          : prevData.defaultUser,

        // Ensure root-level fields update properly
        ...(!isDefaultUserField ? { [field]: value } : {}),
      };
    });
  };

  const handleClick = async () => {
    if (!data) return;
    const isValid = validateTenant(data, isEdit);

    if (!isValid.valid) {
      setError({});
      for (
        let i = 0, item: { name: string; error: string };
        !!(item = isValid.errors[i++]);

      ) {
        setError((prevState) => ({ ...prevState, [item.name]: item.error }));
      }
      return;
    }
    if (selectedResidentStatus) {
      const residentSettings = {
        name: "residentStatus",
        value: selectedResidentStatus?.code
      }
      await createSettings(dispatch, residentSettings)
    }

    await handleSubmit(data);
    setError({});
  };

  const handleCloseTenantDialog = () => {
    handleClose();
    setError({});
  };

  const dialogFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          variant="primary"
          size="md"
          onClick={handleClick}
          disabled={isLoading}
          isLoading={isLoading}
        >
          {localization?.Submit || "Submit"}
        </Button>
        <Button className="text-gold" variant="secondary" size="md" onClick={handleCloseTenantDialog}>
          {localization?.Cancel || "Cancel"}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      visible={open}
      onHide={handleCloseTenantDialog}
      style={{ width: '90%', maxWidth: '40rem' }}
      header={isEdit ? (localization?.EditTenant || "Edit Tenant") : (localization?.CreateTenant || "Create Tenant")}
      footer={dialogFooter}
      modal
      dismissableMask
      className="p-fluid"
    >
      <div className="grid grid-cols-1 gap-4 pt-4 px-0 pb-0">
        {inputTemp.map(({ type, name, label, placeholder, options }) => {
          const value = data
            ? ["emailAddress", "userName"].includes(name)
              ? (data as Record<string, any>)?.defaultUser?.[name] ?? ""
              : (data as Record<string, any>)[name] ?? ""
            : "";

          return (
            <div key={name}>
              {type === "text" ? (
                <Input
                  id={name}
                  value={value}
                  placeholder={placeholder}
                  label={label}
                  onChange={(e) =>
                    handleInputChange(name as keyof TenantModel, e.target.value)
                  }
                  error={(error as Record<string, any>)[name]}
                />
              ) : type === "checkbox" ? (
                <div className="flex items-center gap-2 py-3 border border-gray-200 rounded-md px-3 bg-gray-50">
                  <div className="w-5 h-5 relative flex items-center justify-center">
                    <Checkbox
                      id={name}
                      checked={
                        data ? (data as Record<string, any>)[name] : false
                      }
                      onChange={(e) =>
                        handleInputChange(name as keyof TenantModel, e.checked)
                      }
                      className="border-2 border-gray-400"
                    />
                  </div>
                  <label
                    htmlFor={name}
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                  >
                    {label}
                  </label>
                </div>
              ) : type === "select" ? (
                <div className="mb-4">
                  <label
                    htmlFor={name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {label}
                  </label>
                  <SelectButton
                    id={name}
                    value={data ? (data as Record<string, any>)[name] : null}
                    options={options || []}
                    onChange={(e) =>
                      handleInputChange(name as keyof TenantModel, e.value)
                    }
                    className="w-full"
                  />
                  {(error as Record<string, any>)[name] && (
                    <small className="text-xs text-red-500 mt-1 block">
                      {(error as Record<string, any>)[name]}
                    </small>
                  )}
                </div>
              ) : type === "dropdown" ? ( // ✅ Add dropdown handler
                <div className="mb-4">
                  <label
                    htmlFor={name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {label}
                  </label>
                  <Dropdown
                    id={name}
                    value={data ? (data as Record<string, any>)[name] : null}
                    onChange={(e) =>
                      handleInputChange(name as keyof TenantModel, e.value)
                    }
                    options={options || []}
                    optionLabel="name"
                    placeholder={placeholder}
                    className="w-full md:w-14rem"
                  />
                  {(error as Record<string, any>)[name] && (
                    <small className="text-xs text-red-500 mt-1 block">
                      {(error as Record<string, any>)[name]}
                    </small>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="text-sm text-gray-500 mt-2">{localization?.RequiredFields || "* Required fields"}</div>
      </div>
    </Dialog>
  );
};